import { $ctx, $prose } from '@milkdown/utils';
import { commandsCtx } from '@milkdown/core';
import { diffPluginKey, getPendingChanges, acceptDiffRangeCmd, rejectDiffRangeCmd } from '@milkdown/plugin-diff';
import { DOMSerializer } from '@milkdown/prose/model';
import { PluginKey, Plugin } from '@milkdown/prose/state';
import { Decoration, DecorationSet } from '@milkdown/prose/view';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
function withMeta(plugin, meta) {
  Object.assign(plugin, {
    meta: __spreadValues({
      package: "@milkdown/components"
    }, meta)
  });
  return plugin;
}

const DIFF_CLASS_PREFIX = "milkdown-diff";
const defaultConfig = {
  acceptLabel: "Accept",
  rejectLabel: "Reject",
  customBlockTypes: []
};
const diffComponentConfig = $ctx(defaultConfig, "diffComponentConfig");
withMeta(diffComponentConfig, {
  displayName: "Ctx<diffComponentConfig>",
  group: "DiffComponent"
});

function isBlockSpanning(doc, from, to) {
  if (from === to) return false;
  const $from = doc.resolve(from);
  const $to = doc.resolve(to);
  const fromIndex = $from.index(0);
  const toIndex = $to.depth === 0 ? Math.max(0, $to.index(0) - 1) : $to.index(0);
  return fromIndex !== toIndex;
}
function hasBlockContent(doc, from, to) {
  if (from >= to) return false;
  const $from = doc.resolve(from);
  const $to = doc.resolve(to);
  if ($from.sameParent($to) && $from.parent.isTextblock) return false;
  let found = false;
  doc.nodesBetween(from, to, (node, pos) => {
    if (found) return false;
    if (!node.isBlock) return true;
    const nodeEnd = pos + node.nodeSize;
    if (pos >= from && nodeEnd <= to) {
      found = true;
      return false;
    }
    return true;
  });
  return found;
}
function coversOnlyTrailingEmptyParagraphs(doc, from, to) {
  if (to !== doc.content.size) return false;
  const $from = doc.resolve(from);
  if ($from.depth !== 0) return false;
  for (let i = $from.index(0); i < doc.childCount; i++) {
    const child = doc.child(i);
    if (child.type.name !== "paragraph" || child.content.size > 0) return false;
  }
  return true;
}
function trailingEmptyParagraphStart(doc) {
  let start = doc.content.size;
  for (let i = doc.childCount - 1; i >= 0; i--) {
    const child = doc.child(i);
    if (child.type.name !== "paragraph" || child.content.size > 0) break;
    start -= child.nodeSize;
  }
  return start;
}
function snapToBlockBoundary(doc, pos) {
  const $pos = doc.resolve(pos);
  for (let d = $pos.depth; d >= 1; d--) {
    const parent = $pos.node(d);
    if (parent.isTextblock) {
      return $pos.before(d);
    }
  }
  return pos;
}
function forEachTopLevelNodeInRange(doc, from, to, callback) {
  let pos = 0;
  for (let i = 0; i < doc.childCount; i++) {
    const child = doc.child(i);
    const nodeEnd = pos + child.nodeSize;
    if (pos >= to) break;
    if (nodeEnd > from && pos < to) callback(child, pos, nodeEnd);
    pos = nodeEnd;
  }
}
function addBlockDeletionDecorations(doc, from, to, decorations) {
  forEachTopLevelNodeInRange(doc, from, to, (node, start, end) => {
    if (end === doc.content.size && node.type.name === "paragraph" && node.content.size === 0)
      return;
    decorations.push(
      Decoration.node(start, end, {
        class: `${DIFF_CLASS_PREFIX}-removed-block`
      })
    );
  });
}
function getTopLevelBlockRange(doc, pos, endBoundary = false) {
  if (pos < 0 || pos > doc.content.size) return null;
  const $pos = doc.resolve(Math.min(pos, doc.content.size));
  if ($pos.depth >= 1) {
    return {
      from: $pos.before(1),
      to: $pos.after(1)
    };
  }
  if (endBoundary) {
    const nodeBefore = $pos.nodeBefore;
    if (nodeBefore) {
      return { from: pos - nodeBefore.nodeSize, to: pos };
    }
    const nodeAfter = $pos.nodeAfter;
    if (nodeAfter) {
      return { from: pos, to: pos + nodeAfter.nodeSize };
    }
  } else {
    const nodeAfter = $pos.nodeAfter;
    if (nodeAfter) {
      return { from: pos, to: pos + nodeAfter.nodeSize };
    }
    const nodeBefore = $pos.nodeBefore;
    if (nodeBefore) {
      return { from: pos - nodeBefore.nodeSize, to: pos };
    }
  }
  return null;
}
function getCustomBlockAncestor(doc, pos, customBlockTypes) {
  if (pos < 0 || pos > doc.content.size) return null;
  const $pos = doc.resolve(Math.min(pos, doc.content.size));
  for (let d = $pos.depth; d >= 0; d--) {
    const name = $pos.node(d).type.name;
    if (customBlockTypes.has(name)) return name;
  }
  return null;
}
function getCustomBlockAt(doc, pos, customBlockTypes, endBoundary = false) {
  const ancestor = getCustomBlockAncestor(doc, pos, customBlockTypes);
  if (ancestor) return ancestor;
  const $pos = doc.resolve(Math.min(Math.max(pos, 0), doc.content.size));
  const sibling = endBoundary ? $pos.nodeBefore : $pos.nodeAfter;
  if (sibling && customBlockTypes.has(sibling.type.name))
    return sibling.type.name;
  return null;
}
function collectTopLevelNodes(doc, from, to) {
  const nodes = [];
  let aligned = true;
  let firstStart = -1;
  let lastEnd = -1;
  forEachTopLevelNodeInRange(doc, from, to, (node, start, end) => {
    if (firstStart === -1) firstStart = start;
    lastEnd = end;
    nodes.push(node);
  });
  if (nodes.length === 0 || firstStart !== from || lastEnd !== to) {
    aligned = false;
  }
  return aligned ? nodes : [];
}

function overlaps(a1, a2, b1, b2) {
  return a1 < b2 && a2 > b1;
}
function touchesCustomBlockRange(doc, from, to, customBlockTypes) {
  if (from === to) {
    return getCustomBlockAncestor(doc, from, customBlockTypes) != null;
  }
  return getCustomBlockAt(doc, from, customBlockTypes) != null || getCustomBlockAt(doc, to, customBlockTypes, true) != null;
}
function splitCrossBoundaryChange(doc, newDoc, change) {
  const $fromA = doc.resolve(change.fromA);
  if (!$fromA.parent.isTextblock || $fromA.depth < 1) return null;
  if (!$fromA.node(1).isTextblock) return null;
  const blockEndA = $fromA.after(1);
  const $fromB = newDoc.resolve(change.fromB);
  let splitB;
  if ($fromB.depth >= 1 && $fromB.node(1).isTextblock) {
    splitB = $fromB.after(1);
    if (splitB > change.toB) splitB = change.toB;
  } else {
    splitB = change.fromB;
  }
  if (blockEndA >= change.toA && splitB >= change.toB) return null;
  const segments = [];
  if (blockEndA > change.fromA || splitB > change.fromB) {
    segments.push({
      fromA: change.fromA,
      toA: Math.min(blockEndA, change.toA),
      fromB: change.fromB,
      toB: splitB,
      isBlock: false
    });
  }
  if (change.toA > blockEndA || change.toB > splitB) {
    segments.push({
      fromA: blockEndA,
      toA: Math.max(blockEndA, change.toA),
      fromB: splitB,
      toB: change.toB,
      isBlock: true
    });
  }
  return segments.length > 1 ? segments : null;
}
function changeTouchesCustomBlock(change, doc, newDoc, customBlockTypes) {
  return touchesCustomBlockRange(doc, change.fromA, change.toA, customBlockTypes) || touchesCustomBlockRange(newDoc, change.fromB, change.toB, customBlockTypes);
}
function mergeBlockChanges(pending, doc, newDoc, customBlockTypes) {
  var _a, _b, _c, _d;
  const result = [];
  const consumed = /* @__PURE__ */ new Set();
  for (let i = 0; i < pending.length; i++) {
    if (consumed.has(i)) continue;
    const change = pending[i];
    if (!changeTouchesCustomBlock(change, doc, newDoc, customBlockTypes)) {
      result.push({
        fromA: change.fromA,
        toA: change.toA,
        fromB: change.fromB,
        toB: change.toB,
        isCustomBlock: false
      });
      continue;
    }
    const blockRangeA = expandToCustomBlockRange(
      doc,
      change.fromA,
      change.toA,
      customBlockTypes
    );
    const blockRangeB = expandToCustomBlockRange(
      newDoc,
      change.fromB,
      change.toB,
      customBlockTypes
    );
    const merged = {
      fromA: Math.min((_a = blockRangeA == null ? void 0 : blockRangeA.from) != null ? _a : change.fromA, change.fromA),
      toA: Math.max((_b = blockRangeA == null ? void 0 : blockRangeA.to) != null ? _b : change.toA, change.toA),
      fromB: Math.min((_c = blockRangeB == null ? void 0 : blockRangeB.from) != null ? _c : change.fromB, change.fromB),
      toB: Math.max((_d = blockRangeB == null ? void 0 : blockRangeB.to) != null ? _d : change.toB, change.toB),
      isCustomBlock: true
    };
    consumed.add(i);
    for (let j = i + 1; j < pending.length; j++) {
      if (consumed.has(j)) continue;
      const other = pending[j];
      const overlapA = blockRangeA && overlaps(other.fromA, other.toA, blockRangeA.from, blockRangeA.to);
      const overlapB = blockRangeB && overlaps(other.fromB, other.toB, blockRangeB.from, blockRangeB.to);
      if (!overlapA && !overlapB) continue;
      consumed.add(j);
      merged.fromA = Math.min(merged.fromA, other.fromA);
      merged.toA = Math.max(merged.toA, other.toA);
      merged.fromB = Math.min(merged.fromB, other.fromB);
      merged.toB = Math.max(merged.toB, other.toB);
    }
    const absorbedIndexes = [];
    for (let k = 0; k < result.length; k++) {
      const prev = result[k];
      if (!prev.isCustomBlock) continue;
      const touchesA = overlaps(merged.fromA, merged.toA, prev.fromA, prev.toA);
      const touchesB = overlaps(merged.fromB, merged.toB, prev.fromB, prev.toB);
      if (!touchesA && !touchesB) continue;
      merged.fromA = Math.min(merged.fromA, prev.fromA);
      merged.toA = Math.max(merged.toA, prev.toA);
      merged.fromB = Math.min(merged.fromB, prev.fromB);
      merged.toB = Math.max(merged.toB, prev.toB);
      absorbedIndexes.push(k);
    }
    for (let k = absorbedIndexes.length - 1; k >= 0; k--) {
      result.splice(absorbedIndexes[k], 1);
    }
    result.push(merged);
  }
  return result;
}
function anchorTrailingInsertsBeforeEmptyParagraph(changes, doc) {
  const trailingStart = trailingEmptyParagraphStart(doc);
  if (trailingStart === doc.content.size) return;
  for (const change of changes) {
    const isPureInsert = change.fromA === change.toA && change.fromB < change.toB;
    if (isPureInsert && change.fromA >= trailingStart) {
      change.fromA = trailingStart;
      change.toA = trailingStart;
    }
  }
}
function expandToCustomBlockRange(doc, from, to, customBlockTypes) {
  if (from === to) {
    if (getCustomBlockAncestor(doc, from, customBlockTypes) == null) return null;
    return getTopLevelBlockRange(doc, from);
  }
  if (getCustomBlockAt(doc, from, customBlockTypes) != null) {
    return getTopLevelBlockRange(doc, from);
  }
  if (getCustomBlockAt(doc, to, customBlockTypes, true) != null) {
    return getTopLevelBlockRange(doc, to, true);
  }
  return null;
}

const diffDecorationKey = new PluginKey(
  "MILKDOWN_DIFF_DECORATION"
);
const diffDecorationPlugin = $prose((ctx) => {
  const config = ctx.get(diffComponentConfig.key);
  return new Plugin({
    key: diffDecorationKey,
    state: {
      init: () => DecorationSet.empty,
      apply(tr, decorations, _oldState, newState) {
        const diffState = diffPluginKey.getState(newState);
        if (!(diffState == null ? void 0 : diffState.active)) return DecorationSet.empty;
        if (tr.getMeta(diffPluginKey) || tr.docChanged)
          return buildDecorations(ctx, newState.doc, diffState, config);
        return decorations.map(tr.mapping, tr.doc);
      }
    },
    props: {
      decorations(state) {
        var _a;
        return (_a = diffDecorationKey.getState(state)) != null ? _a : DecorationSet.empty;
      }
    }
  });
});
withMeta(diffDecorationPlugin, {
  displayName: "Prose<diffDecoration>",
  group: "DiffComponent"
});
function addCrossBoundaryDecorations({
  doc,
  newDoc,
  segments,
  change,
  changeIndex,
  commands,
  acceptLabel,
  rejectLabel,
  decorations
}) {
  for (let j = 0; j < segments.length; j++) {
    const seg = segments[j];
    const segDeletion = seg.fromA < seg.toA;
    const segInsertion = seg.fromB < seg.toB;
    if (segDeletion) {
      if (seg.isBlock) {
        addBlockDeletionDecorations(doc, seg.fromA, seg.toA, decorations);
      } else {
        decorations.push(
          Decoration.inline(seg.fromA, seg.toA, {
            class: `${DIFF_CLASS_PREFIX}-removed`
          })
        );
      }
    }
    if (segInsertion) {
      const widgetPos = seg.isBlock ? snapToBlockBoundary(doc, segDeletion ? seg.toA : seg.fromA) : segDeletion ? seg.toA : seg.fromA;
      const widget = createInsertedWidget(newDoc, seg, seg.isBlock);
      decorations.push(
        Decoration.widget(widgetPos, widget, {
          side: -1,
          key: `added-${changeIndex}-${j}`
        })
      );
    }
  }
  const lastSeg = segments[segments.length - 1];
  const lastSegEnd = lastSeg.isBlock ? lastSeg.fromA < lastSeg.toA ? lastSeg.toA : lastSeg.fromA : change.toA;
  const controlsPos = snapToBlockBoundary(doc, lastSegEnd);
  const controls = createControlsWidget({
    commands,
    isBlockLevel: true,
    change,
    acceptLabel,
    rejectLabel
  });
  decorations.push(
    Decoration.widget(controlsPos, controls, {
      side: -1,
      key: `controls-${changeIndex}`
    })
  );
}
function buildDecorations(ctx, doc, diffState, config) {
  const decorations = [];
  const commands = ctx.get(commandsCtx);
  const customBlockTypes = new Set(config.customBlockTypes);
  const pending = getPendingChanges(diffState);
  const mergedChanges = mergeBlockChanges(
    pending,
    doc,
    diffState.newDoc,
    customBlockTypes
  );
  anchorTrailingInsertsBeforeEmptyParagraph(mergedChanges, doc);
  for (let i = 0; i < mergedChanges.length; i++) {
    const change = mergedChanges[i];
    const isDeletion = change.fromA < change.toA;
    const isInsertion = change.fromB < change.toB;
    if (isDeletion && !isInsertion && coversOnlyTrailingEmptyParagraphs(doc, change.fromA, change.toA))
      continue;
    const deletionSpansBlocks = isDeletion && isBlockSpanning(doc, change.fromA, change.toA);
    const deletionHasBlocks = isDeletion && hasBlockContent(doc, change.fromA, change.toA);
    const insertionHasBlocks = isInsertion && hasBlockContent(diffState.newDoc, change.fromB, change.toB);
    const deletionWithinSingleBlock = isDeletion && !deletionSpansBlocks && !deletionHasBlocks && !change.isCustomBlock && !insertionHasBlocks;
    const isBlockLevel = (deletionSpansBlocks || deletionHasBlocks || insertionHasBlocks) && !deletionWithinSingleBlock;
    if (isBlockLevel && !change.isCustomBlock) {
      const segments = splitCrossBoundaryChange(doc, diffState.newDoc, change);
      if (segments) {
        addCrossBoundaryDecorations({
          doc,
          newDoc: diffState.newDoc,
          segments,
          change,
          changeIndex: i,
          commands,
          acceptLabel: config.acceptLabel,
          rejectLabel: config.rejectLabel,
          decorations
        });
        continue;
      }
    }
    if (isDeletion) {
      if (change.isCustomBlock || isBlockLevel) {
        addBlockDeletionDecorations(doc, change.fromA, change.toA, decorations);
      } else {
        decorations.push(
          Decoration.inline(change.fromA, change.toA, {
            class: `${DIFF_CLASS_PREFIX}-removed`
          })
        );
      }
    }
    const rawWidgetPos = isDeletion ? change.toA : change.fromA;
    const widgetPos = isBlockLevel ? snapToBlockBoundary(doc, rawWidgetPos) : rawWidgetPos;
    if (isInsertion) {
      const widget = createInsertedWidget(
        diffState.newDoc,
        change,
        isBlockLevel
      );
      decorations.push(
        Decoration.widget(widgetPos, widget, { side: -1, key: `added-${i}` })
      );
    }
    const controls = createControlsWidget({
      commands,
      isBlockLevel,
      change,
      acceptLabel: config.acceptLabel,
      rejectLabel: config.rejectLabel
    });
    decorations.push(
      Decoration.widget(widgetPos, controls, {
        side: isBlockLevel ? -1 : 1,
        key: `controls-${i}`
      })
    );
  }
  return DecorationSet.create(doc, decorations);
}
function createInsertedWidget(newDoc, change, isBlockLevel) {
  var _a;
  const dom = document.createElement(isBlockLevel ? "div" : "span");
  dom.className = `${DIFF_CLASS_PREFIX}-added`;
  dom.contentEditable = "false";
  if (isBlockLevel) dom.classList.add(`${DIFF_CLASS_PREFIX}-added-block`);
  const serializer = DOMSerializer.fromSchema(newDoc.type.schema);
  if (isBlockLevel) {
    const nodes = collectTopLevelNodes(newDoc, change.fromB, change.toB);
    if (nodes.length > 0) {
      const fragment2 = document.createDocumentFragment();
      for (const node of nodes) {
        fragment2.appendChild(serializer.serializeNode(node));
      }
      dom.appendChild(fragment2);
      return dom;
    }
  }
  const slice = newDoc.slice(change.fromB, change.toB);
  const fragment = serializer.serializeFragment(slice.content);
  dom.appendChild(fragment);
  if (!((_a = dom.textContent) == null ? void 0 : _a.trim()) && !dom.querySelector("img, video, audio, canvas, svg")) {
    const fallback = newDoc.textBetween(change.fromB, change.toB, "\n", "\n");
    if (fallback.trim()) {
      dom.textContent = fallback;
    }
  }
  return dom;
}
function createControlsWidget({
  commands,
  isBlockLevel,
  change,
  acceptLabel,
  rejectLabel
}) {
  const dom = document.createElement(isBlockLevel ? "div" : "span");
  dom.className = `${DIFF_CLASS_PREFIX}-controls`;
  dom.contentEditable = "false";
  if (isBlockLevel) dom.classList.add(`${DIFF_CLASS_PREFIX}-controls-block`);
  const handler = (action) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const range = {
      fromA: change.fromA,
      toA: change.toA,
      fromB: change.fromB,
      toB: change.toB
    };
    const key = action === "accept" ? acceptDiffRangeCmd.key : rejectDiffRangeCmd.key;
    commands.call(key, range);
  };
  const acceptBtn = document.createElement("button");
  acceptBtn.className = `${DIFF_CLASS_PREFIX}-accept`;
  acceptBtn.textContent = acceptLabel;
  acceptBtn.addEventListener("click", handler("accept"));
  const rejectBtn = document.createElement("button");
  rejectBtn.className = `${DIFF_CLASS_PREFIX}-reject`;
  rejectBtn.textContent = rejectLabel;
  rejectBtn.addEventListener("click", handler("reject"));
  dom.appendChild(acceptBtn);
  dom.appendChild(rejectBtn);
  return dom;
}

const diffComponent = [
  diffComponentConfig,
  diffDecorationPlugin
];

export { DIFF_CLASS_PREFIX, diffComponent, diffComponentConfig, diffDecorationPlugin };
//# sourceMappingURL=index.js.map
